import React, { useState, useEffect } from 'react';
import { Calendar, Save } from 'lucide-react';
import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';
import {
  ORG_DEFAULT_DATE_RANGE_OPTIONS,
  notifyOrgDateSettingsUpdated,
} from '../../../../../shared/frontend/dateRange';

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

/**
 * Organization-level default date range for ALL RCM services
 * (Dashboard, lists, analytics). Source of truth: organizations.settings.rcm
 */
export default function OrganizationDefaultDateRangeSection({
  organization,
  onMessage,
  onSaved,
}) {
  const organizationId = organization?.id || organization?.organization_id;
  const [range, setRange] = useState('30');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const rcm = organization?.settings?.rcm;
    if (!rcm) {
      setRange('30');
      setDateFrom('');
      setDateTo('');
      return;
    }
    const dr = String(rcm.default_date_range || '30').trim();
    setRange(['30', '90', '180', 'custom'].includes(dr) ? dr : '30');
    setDateFrom(rcm.default_date_from ? String(rcm.default_date_from).slice(0, 10) : '');
    setDateTo(rcm.default_date_to ? String(rcm.default_date_to).slice(0, 10) : '');
  }, [organization]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!organizationId) return;
    if (range === 'custom' && dateFrom && dateTo && dateFrom > dateTo) {
      onMessage?.({ type: 'error', text: 'Start date must be on or before end date.' });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getSaasApiBaseUrl();
      const res = await fetch(`${API_BASE_URL}/organizations/${organizationId}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          rcm: {
            default_date_range: range,
            default_date_from: range === 'custom' ? dateFrom || null : null,
            default_date_to: range === 'custom' ? dateTo || null : null,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err));
      }
      notifyOrgDateSettingsUpdated();
      onMessage?.({
        type: 'success',
        text: 'Default date range saved for all RCM services.',
      });
      onSaved?.();
    } catch (err) {
      onMessage?.({ type: 'error', text: err.message || 'Failed to save date range' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900">Default date range (all RCM apps)</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Controls the default lookback on Remittance Advice, Claims Processing, Denial Management,
          AR Posting, and other RCM dashboards and lists. Users can still change the range on each page.
        </p>
      </div>
      <div className="px-6 py-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {ORG_DEFAULT_DATE_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              onClick={() => setRange(opt.value)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-60 ${
                range === opt.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {range === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !organizationId || (range === 'custom' && (!dateFrom || !dateTo))}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save default date range'}
        </button>
      </div>
    </div>
  );
}
